import type {
  ExternalChatConnector,
  HubChatMessage,
  HubChatSystemId,
  HubPresence,
  HubReaction,
  HubReadReceipt,
} from "@/chat/model";
import { emojiForReactionName } from "@/lib/emoji";

const SLACK_TS = /^\d+\.\d+$/;

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function nested(row: Record<string, unknown>, key: string): Record<string, unknown> | undefined {
  const value = row[key];
  return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

function parseTime(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value < 1e12 ? Math.round(value * 1000) : value;
    }
    if (typeof value === "string" && value.trim()) {
      if (SLACK_TS.test(value)) return Math.round(Number(value) * 1000);
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

export function presenceFrom(raw: unknown): HubPresence | undefined {
  const text = pickString(raw).toLowerCase();
  if (text === "active" || text === "online") return "online";
  if (text === "away") return "away";
  if (text === "dnd" || text === "do_not_disturb") return "dnd";
  if (text === "offline") return "offline";
  return undefined;
}

export function emojiFromShortcode(name: string): string {
  const key = name.replace(/^:|:$/g, "").trim().toLowerCase();
  return emojiForReactionName(key) || `:${key}:`;
}

// `mine` comes from the API (the connected account is in the reaction's users) — never from the author.
function reactionsFrom(raw: unknown): HubReaction[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: HubReaction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const name = pickString(row.name, row.emoji, row.reaction);
    if (!name) continue;
    const count = typeof row.count === "number" ? row.count : Array.isArray(row.users) ? row.users.length : 1;
    out.push({
      emoji: emojiFromShortcode(name),
      name: name.replace(/^:|:$/g, ""),
      count,
      mine: row.mine === true || row.me === true,
    });
  }
  return out.length ? out : undefined;
}

function readByFrom(raw: unknown): HubReadReceipt[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: HubReadReceipt[] = [];
  for (const item of raw) {
    if (typeof item === "string" && item.trim()) {
      out.push({ name: item.trim() });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const name = pickString(row.name, row.displayName, row.real_name);
    if (!name) continue;
    out.push({ name, userId: pickString(row.userId, row.id) || undefined });
  }
  return out.length ? out : undefined;
}

function avatarFrom(row: Record<string, unknown>): string {
  const user = nested(row, "user");
  const sender = nested(row, "sender");
  const profile = nested(row, "user_profile") || nested(row, "profile");
  return pickString(
    row.avatarUrl,
    row.avatar,
    row.image,
    row.image_url,
    row.pic_url,
    user?.image_72,
    user?.avatarUrl,
    user?.image_url,
    profile?.image_72,
    profile?.image_48,
    sender?.avatarUrl,
    sender?.avatar,
    sender?.image_url,
    sender?.image,
  );
}

function baseMessage(
  system: HubChatSystemId,
  row: Record<string, unknown>,
  index: number,
): HubChatMessage | null {
  const user = nested(row, "user");
  const sender = nested(row, "sender");
  const text = pickString(row.text, row.body, row.message, typeof row.content === "string" ? row.content : undefined);
  const author = pickString(
    row.author,
    row.userName,
    row.user_name,
    row.username,
    user?.name,
    user?.real_name,
    sender?.name,
    typeof row.user === "string" ? row.user : undefined,
  );
  const id = pickString(row.id, row.ts, row.messageId) || `msg-${index}`;
  if (!text && !author) return null;
  const userId = pickString(row.userId, row.user_id, user?.id, sender?.id);
  const avatarUrl = avatarFrom(row);
  const selfFlag = [row.self, row.isSelf, row.fromMe, row.mine].find((value) => typeof value === "boolean");
  const ts = typeof row.ts === "string" && SLACK_TS.test(row.ts) ? row.ts : id.match(/_(\d+\.\d+)$/)?.[1];
  const message: HubChatMessage = {
    id,
    author: author || "Unknown",
    text,
    at: parseTime(row.at, row.sentAt, row.ts, row.time, row.timestamp),
    system,
  };
  if (userId) message.userId = userId;
  if (/^https:\/\//i.test(avatarUrl)) message.avatarUrl = avatarUrl;
  if (typeof selfFlag === "boolean") message.self = selfFlag;
  if (ts) message.ts = ts;
  if (row.edited === true) message.edited = true;
  const presence = presenceFrom(row.presence ?? user?.presence ?? sender?.presence);
  if (presence) message.presence = presence;
  const reactions = reactionsFrom(row.reactions);
  if (reactions) message.reactions = reactions;
  if (typeof row.threadTs === "string" && SLACK_TS.test(row.threadTs)) message.threadTs = row.threadTs;
  if (typeof row.replyCount === "number" && row.replyCount > 0) message.replyCount = row.replyCount;
  if (typeof row.parentTs === "string" && SLACK_TS.test(row.parentTs)) message.parentTs = row.parentTs;
  const readBy = readByFrom(row.readBy ?? row.read_by ?? row.seenBy);
  if (readBy) message.readBy = readBy;
  const statusText = pickString(row.statusText, row.status_text, user?.statusText, sender?.statusText);
  if (statusText) message.statusText = statusText;
  const timeZone = pickString(row.timeZone, row.tzLabel, row.tz, user?.tzLabel);
  if (timeZone) message.timeZone = timeZone;
  if (Array.isArray(row.files)) {
    const files = row.files
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const file = item as Record<string, unknown>;
        const url = pickString(file.url, file.permalink, file.url_private);
        const name = pickString(file.name, file.title, "File");
        return /^https:\/\//i.test(url) ? { name, url } : null;
      })
      .filter((file): file is { name: string; url: string } => Boolean(file));
    if (files.length) message.files = files;
  }
  return message;
}

export const slackConnector: ExternalChatConnector = {
  id: "slack",
  matches: (row) => row.providerId === "slack" || typeof row.ts === "string",
  toMessage: (row, index) => baseMessage("slack", row, index),
};

export const cliqConnector: ExternalChatConnector = {
  id: "cliq",
  matches: (row) => row.providerId === "cliq" || Boolean(nested(row, "sender") && row.content),
  toMessage: (row, index) => {
    const content = nested(row, "content");
    const next = content?.text ? { ...row, text: pickString(row.text, content.text) } : row;
    return baseMessage("cliq", next, index);
  },
};

export const zoomConnector: ExternalChatConnector = {
  id: "zoom",
  matches: (row) => row.providerId === "zoom",
  toMessage: (row, index) => baseMessage("zoom", row, index),
};

export const teamsConnector: ExternalChatConnector = {
  id: "teams",
  matches: (row) => row.providerId === "teams",
  toMessage: (row, index) => baseMessage("teams", row, index),
};

export const googleConnector: ExternalChatConnector = {
  id: "google",
  matches: (row) => row.providerId === "google",
  toMessage: (row, index) => baseMessage("google", row, index),
};

const CONNECTORS = [googleConnector, slackConnector, cliqConnector, zoomConnector, teamsConnector];

/** Map one external row into Hub chat. Unknown systems still land in the same shape. */
export function toHubChatMessage(raw: unknown, index: number): HubChatMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const connector = CONNECTORS.find((item) => item.matches(row));
  return (connector ?? { id: "hub" as const, matches: () => true, toMessage: (value: Record<string, unknown>, i: number) => baseMessage("hub", value, i) }).toMessage(row, index);
}

export function typingNamesFrom(payload: unknown): string[] {
  if (!payload || typeof payload !== "object") return [];
  const raw = (payload as { typing?: unknown }).typing;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (typeof item === "string" ? item : pickString((item as { name?: unknown })?.name)))
    .filter(Boolean);
}
